/* ============================================================
   Recently Viewed Products
   - Tracks the current product's handle in localStorage whenever a
     product page loads (see trackCurrentProduct below).
   - The <recently-viewed-products> custom element then fetches each
     stored product's own page via Shopify's Section Rendering API
     (?section_id=recently-viewed-item) and injects the returned card
     markup. This guarantees the cards are fully accurate — correct
     price/currency formatting, sale/sold-out badges, and working
     Eye / Add-to-cart buttons — since they're rendered server-side by
     Shopify itself, not reconstructed by hand in JavaScript.
   ============================================================ */

(function () {
    'use strict';

    const STORAGE_KEY = 'wp_recently_viewed';
    const MAX_STORED = 12;

    function trackCurrentProduct() {
        const main = document.querySelector('main[data-template]');
        if (!main || main.dataset.template !== 'product') return;

        const productInfo = document.querySelector('product-info[data-url]');
        const handle = productInfo?.dataset.url;
        if (!handle) return;

        let stored = [];
        try {
            stored = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
        } catch (e) {
            stored = [];
        }

        stored = Array.isArray(stored) ? stored.filter((url) => typeof url === 'string' && url !== handle) : [];
        stored.unshift(handle);
        stored = stored.slice(0, MAX_STORED);

        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
        } catch (e) {
            // localStorage may be unavailable (private browsing quota, etc). Fail
            // silently; this feature is a nice-to-have, not critical path.
        }
    }

    function getStoredProductUrls(excludeUrl, limit) {
        let stored = [];
        try {
            stored = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
        } catch (e) {
            stored = [];
        }
        return Array.isArray(stored)
            ? stored.filter((url) => typeof url === 'string' && /^\/([a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/?#]+$/i.test(url) && url !== excludeUrl).slice(0, limit)
            : [];
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', trackCurrentProduct);
    } else {
        trackCurrentProduct();
    }

    if (!customElements.get('recently-viewed-products')) {
        customElements.define(
            'recently-viewed-products',
            class RecentlyViewedProducts extends HTMLElement {
                connectedCallback() {
                    this.grid = this.querySelector('[data-recently-viewed-grid]');
                    if (!this.grid) return;

                    const currentUrl = this.dataset.currentUrl;
                    const limit = parseInt(this.dataset.limit, 10) || 4;
                    const urls = getStoredProductUrls(currentUrl, limit);

                    if (urls.length === 0) {
                        this.remove();
                        return;
                    }

                    this.loadCards(urls);
                }

                loadCards(urls) {
                    const requests = urls.map((url) =>
                        fetch(`${url}?section_id=recently-viewed-item`)
                            .then((response) => (response.ok ? response.text() : null))
                            .then((text) => {
                                if (!text) return null;
                                const doc = new DOMParser().parseFromString(text, 'text/html');
                                // Sections requested via the Section Rendering API
                                // (?section_id=...) are always wrapped by Shopify in
                                // `#shopify-section-{section_id}`, whether or not the
                                // section defines a {% schema %} block.
                                return doc.querySelector('#shopify-section-recently-viewed-item');
                            })
                            .catch(() => null)
                    );

                    Promise.all(requests).then((sections) => {
                        const validSections = sections.filter(
                            (section) => section && section.querySelector('.card-wrapper')
                        );

                        if (validSections.length === 0) {
                            this.remove();
                            return;
                        }

                        const fragment = document.createDocumentFragment();
                        validSections.forEach((section) => {
                            const li = document.createElement('li');
                            li.className = 'grid__item';
                            li.innerHTML = section.innerHTML;
                            fragment.appendChild(li);
                        });

                        this.grid.innerHTML = '';
                        this.grid.appendChild(fragment);
                        this.classList.add('recently-viewed-products--loaded');
                        this.classList.remove('recently-viewed-products--loading');
                    });
                }
            }
        );
    }
})();
